import { formatPublishDate } from '@/lib/blog/dates'
import type { BlogPostMeta } from '@/lib/blog/schema'

/**
 * The fields a post card actually renders — the serialization shape for post
 * listings passed to client components. A full `BlogPostMeta` satisfies it
 * (mood listings pass metas straight through); the all-stories view passes a
 * trimmed projection instead, because ~465 posts ride the flight payload and
 * every unused field is paid for per post. `excerpt` is optional for the same
 * reason: mood cards show it, the all-stories cards omit it.
 */
export interface PostCardData {
    slug: string
    title: string
    publishedAt: string
    heroImage?: BlogPostMeta['heroImage']
    excerpt?: string
}

/** Project a full meta onto {@link PostCardData} — what the all-view serializes. */
export function toPostCardData(meta: BlogPostMeta): PostCardData {
    return {
        slug: meta.slug,
        title: meta.title,
        publishedAt: meta.publishedAt,
        ...(meta.heroImage ? { heroImage: meta.heroImage } : {}),
    }
}

/**
 * A vertical post card for the mood filter grid — hero image on top, then
 * title, excerpt (when provided), and date. Links to the bare `/blog/<slug>/`
 * (the post's canonical URL); a plain anchor, not the i18n `Link`, so it
 * targets the un-prefixed canonical directly instead of the `/en-us/...` form
 * that 301s back to it (same policy as `AnnouncementCard`). A post without a
 * hero image gets a plain placeholder block so rows keep their shape.
 *
 * The title is an `<h2>`: the card grid renders under the mood view's single
 * `<h1>` (see `BlogHomeContent`), so an `<h3>` would skip a heading level.
 */
export function MoodPostCard({ post }: { post: PostCardData }) {
    const href = `/blog/${post.slug}/`

    return (
        <article className="group flex h-full flex-col overflow-hidden rounded-lg bg-card shadow-sm transition-shadow duration-300 hover:shadow-md">
            {/* The hero-image link duplicates the title link below (same href), so
                exposing it would add a second tab stop and a second announcement for
                one destination. Hidden from assistive tech and the tab order; the
                title link is the card's one name. (Not about a missing name: every
                committed hero alt is non-empty since #708 — it is the duplication.) */}
            <a href={href} aria-hidden="true" tabIndex={-1} className="block">
                {post.heroImage ? (
                    /* Legacy hotlinked hero (interim media policy — same as post bodies);
                       plain <img>, so no next/image loader config is needed. */
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={post.heroImage.url}
                        alt={post.heroImage.alt}
                        width={post.heroImage.width}
                        height={post.heroImage.height}
                        className="aspect-[3/2] w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                    />
                ) : (
                    <span aria-hidden="true" className="block aspect-[3/2] w-full bg-muted" />
                )}
            </a>
            <div className="flex grow flex-col gap-2 p-5">
                {/* Clamped to two lines so a row of cards keeps one height —
                    the ellipsis is visual only, so the link's accessible name
                    is still the whole title. */}
                <h2 className="line-clamp-2 text-lg font-bold text-text-primary">
                    <a href={href} className="hover:text-text-link">
                        {post.title}
                    </a>
                </h2>
                <span aria-hidden="true" className="h-0.5 w-12 bg-primary" />
                {post.excerpt !== undefined && (
                    <p className="grow text-sm leading-relaxed text-text-secondary">
                        {post.excerpt}
                    </p>
                )}
                <time dateTime={post.publishedAt} className="mt-auto text-sm text-text-secondary">
                    {formatPublishDate(post.publishedAt)}
                </time>
            </div>
        </article>
    )
}
